import React, { useState } from 'react';
import type { CincInspectionResponse } from '../../../api/cinc';
import {
  canInspectCinc,
  createCincInspectionSession,
  type CincInspectionScope,
  type CincInspectionView,
} from '../../../utils/cincInspection';

interface Props {
  scope: CincInspectionScope;
  inspect: () => Promise<CincInspectionResponse>;
}

/** Manual metadata read, separate from all connection and import controls. */
export const CincInspectionPanel: React.FC<Props> = ({ scope, inspect }) => {
  const [, setView] = useState<CincInspectionView>({ key: '', version: 0, phase: 'idle' });
  const sessionRef = React.useRef<ReturnType<typeof createCincInspectionSession> | null>(null);
  if (!sessionRef.current) sessionRef.current = createCincInspectionSession(inspect, setView);
  const view = sessionRef.current.observe(scope);

  if (!canInspectCinc(scope)) return null;
  return (
    <section
      className="mt-6 rounded-lg border border-amber-300 p-4"
      aria-label="Existing CINC metadata inspection"
    >
      <button
        type="button"
        disabled={view.phase === 'loading'}
        onClick={() => void sessionRef.current?.run(scope)}
        className="rounded bg-amber-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
      >
        Inspect existing CINC metadata
      </button>
      <p className="mt-2 text-sm font-semibold">Not qualified for import/outreach</p>
      {view.phase === 'loading' && <p role="status">Reading existing metadata…</p>}
      {view.phase === 'error' && <p role="alert">{view.error}</p>}
      {view.phase === 'result' && view.result && (
        <div className="mt-2 space-y-1 text-sm" role="status">
          <p>Status: {view.result.status}</p>
          <p>Reason: {view.result.reason}</p>
          <p>Existing binding: yes; read only: yes; writes: 0</p>
          <p>Connector found: {String(view.result.metadata.connectorFound)}</p>
          <p>Connector auth type: {view.result.metadata.connectorAuthType ?? 'unknown'}</p>
          <p>Connector auth status: {view.result.metadata.connectorAuthStatus}</p>
          <p>Credential matches: {view.result.metadata.credentialMatchCount}</p>
          <p>Credential valid: {String(view.result.metadata.credentialValid)}</p>
          <p>Profile paths and types:</p>
          <ul className="list-disc pl-5">
            {view.result.metadata.profilePaths.map(({ path, type }) => (
              <li key={path}>
                {path}: {type}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};
